import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getApi } from '../providers/AuthContext';

import PageWrapper from '../components/PageWrapper';
import AccountForm from '../components/Account/Form';
import AccountProfileForm from '../components/Account/ProfileForm';
import Integrations from '../components/Account/Integrations';

import useQuery from '../hooks/useQuery';
import { AlertCircle, X, UserCircle, Plug, Shield } from 'lucide-react';

const sectionVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.35, ease: [0.23, 1, 0.32, 1] as [number, number, number, number] },
  }),
};

const SectionHeader: React.FC<{
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  description: string;
  badge?: React.ReactNode;
}> = ({ icon, iconBg, title, description, badge }) => (
  <div className="flex items-center gap-3 mb-6">
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <h2 className="text-base font-bold text-foreground m-0">{title}</h2>
        {badge}
      </div>
      <p className="text-xs text-muted-foreground m-0 mt-0.5">{description}</p>
    </div>
  </div>
);

const Page: React.FC = () => {
  const query = useQuery();

  const [formUser, setFormUser] = useState(null);
  const [formUserProfile, setFormUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const api = getApi();

  useEffect(() => {
    const loadData = async () => {
      try {
        setError(null);
        const result = await api.get("/api/v1/users/me");
        setFormUser(result.user);
        setFormUserProfile(result.profile);
      } catch {
        setError('Failed to load account data.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  return (
    <PageWrapper pageTitle="Account Settings">
      <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto">
        {/* Error banner */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="flex items-center gap-3 p-4 bg-destructive/[0.06] dark:bg-destructive/[0.12] border border-destructive/20 text-destructive rounded-xl mb-6"
            >
              <div className="w-8 h-8 rounded-lg bg-destructive/10 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-4 h-4" />
              </div>
              <span className="text-sm font-medium flex-1">{error}</span>
              <button onClick={() => setError(null)} className="p-1.5 rounded-lg hover:bg-destructive/10 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {loading ? (
          /* Skeleton loading */
          <div className="space-y-6">
            {/* Account Details skeleton */}
            <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
              <div className="h-1 bg-muted" />
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="skeleton w-10 h-10 rounded-xl" />
                  <div>
                    <div className="skeleton w-32 h-4 rounded mb-1.5" />
                    <div className="skeleton w-48 h-3 rounded" />
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="skeleton w-16 h-3 rounded" />
                  <div className="skeleton w-full h-12 rounded-xl" />
                  <div className="skeleton w-28 h-10 rounded-xl" />
                </div>
                <div className="border-t border-border/40 mt-6 pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="skeleton w-20 h-4 rounded mb-1.5" />
                      <div className="skeleton w-52 h-3 rounded" />
                    </div>
                    <div className="skeleton w-32 h-10 rounded-xl" />
                  </div>
                </div>
              </div>
            </div>

            {/* Profile skeleton */}
            <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
              <div className="h-1 bg-muted" />
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="skeleton w-10 h-10 rounded-xl" />
                  <div>
                    <div className="skeleton w-28 h-4 rounded mb-1.5" />
                    <div className="skeleton w-52 h-3 rounded" />
                  </div>
                </div>
                <div className="flex justify-center mb-6">
                  <div className="skeleton w-28 h-28 rounded-full" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i}>
                      <div className="skeleton w-16 h-3 rounded mb-2" />
                      <div className="skeleton w-full h-12 rounded-xl" />
                    </div>
                  ))}
                </div>
                <div className="mt-4">
                  <div className="skeleton w-10 h-3 rounded mb-2" />
                  <div className="skeleton w-full h-20 rounded-xl" />
                </div>
              </div>
            </div>

            {/* Integrations skeleton */}
            <div className="bg-card border border-border/60 rounded-2xl overflow-hidden">
              <div className="h-1 bg-muted" />
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="skeleton w-10 h-10 rounded-xl" />
                  <div>
                    <div className="skeleton w-24 h-4 rounded mb-1.5" />
                    <div className="skeleton w-56 h-3 rounded" />
                  </div>
                </div>
                <div className="skeleton w-full h-20 rounded-xl" />
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Account Details Section */}
            <motion.div
              custom={0}
              initial="hidden"
              animate="visible"
              variants={sectionVariants}
              className="bg-card border border-border/60 rounded-2xl shadow-soft-sm overflow-hidden"
            >
              <div className="h-1 bg-gradient-to-r from-primary via-accent to-secondary" />
              <div className="p-6">
                <SectionHeader
                  icon={<Shield className="w-5 h-5 text-primary" />}
                  iconBg="bg-primary/10 dark:bg-primary/15"
                  title="Account Details"
                  description="Manage your email and password"
                />
                <AccountForm user={formUser} confirmed={query.get('confirmed') == '1'} />
              </div>
            </motion.div>

            {/* Profile Section */}
            <motion.div
              custom={1}
              initial="hidden"
              animate="visible"
              variants={sectionVariants}
              className="bg-card border border-border/60 rounded-2xl shadow-soft-sm overflow-hidden"
            >
              <div className="h-1 bg-gradient-to-r from-accent via-secondary to-primary" />
              <div className="p-6">
                <SectionHeader
                  icon={<UserCircle className="w-5 h-5 text-accent" />}
                  iconBg="bg-accent/10 dark:bg-accent/15"
                  title="Parent Profile"
                  description="Your public profile information"
                />
                <AccountProfileForm profileState={[formUserProfile, setFormUserProfile]} />
              </div>
            </motion.div>

            {/* Integrations Section */}
            <motion.div
              custom={2}
              initial="hidden"
              animate="visible"
              variants={sectionVariants}
              className="bg-card border border-border/60 rounded-2xl shadow-soft-sm overflow-hidden"
            >
              <div className="h-1 bg-gradient-to-r from-secondary via-primary to-accent" />
              <div className="p-6">
                <SectionHeader
                  icon={<Plug className="w-5 h-5 text-secondary" />}
                  iconBg="bg-secondary/10 dark:bg-secondary/15"
                  title="Integrations"
                  description="Connect external services to enhance your experience"
                />
                <Integrations />
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </PageWrapper>
  );
};

export default Page;
